import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Filter,
  Resize,
  Inject,
  RowDataBoundEventArgs,
} from "@syncfusion/ej2-react-grids";
import EditIcon from "@mui/icons-material/Edit";
// import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import Switch from "@mui/material/Switch";
import { SelectChangeEvent } from "@mui/material/Select";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import { getInsuranceProviders } from "../../../redux/features/insurance-provider/insurance-provider-slice";
import {
  getRates,
  createRate,
  updateRate,
  // deleteRate,
} from "../../../redux/features/rates/rates-slice";
import { getDataCatalog } from "../../../redux/features/data-catalog/data-catalog-slice";
import moment from "moment";
import { DataCatalogConcepts } from "../../../constants/catalog";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import Loader from "../../../layouts/loader/InLoader";
import type { RootState } from "../../../redux/store";
import { GetAllProviders } from "../../../redux/features/providers-main/provider-list-slice";

interface IBussinessRates {
  tabState: string;
}

interface Rate {
  id: string;
  authCode: string;
  modifier: string;
  perType: string;
  currency: string;
  rateBilled: number;
  rateAgreed: number;
  employeeId: string;
  employeeRate: number;
  insuranceProviderId: string;
  insuranceProvider?: {
    id: string;
    insuranceProviderName: string;
  };
  effectiveDate: string;
  expirationDate: string;
  isActive: boolean;
}

interface FormValues {
  authCode: string;
  modifier: string;
  perType: string;
  currency: string;
  rateAgreed: number | string;
  rateBilled: number | string;
  employeeId: string;
  employeeRate: number | string;
  insuranceProviderId: string;
  effectiveDate: string;
  expirationDate: string;
}

const initialFormValues: FormValues = {
  authCode: "",
  modifier: "",
  perType: "Unit",
  currency: "USD",
  rateAgreed: "",
  rateBilled: "",
  employeeId: "",
  employeeRate: "",
  insuranceProviderId: "",
  effectiveDate: moment().format("YYYY-MM-DD"),
  expirationDate: moment().add(1, "year").format("YYYY-MM-DD"),
};

const BusinessRates: React.FC<IBussinessRates> = ({ tabState }) => {
  const dispatch = useAppDispatch();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editRateId, setEditRateId] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);
  // const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  // const [selectedRateId, setSelectedRateId] = useState<string | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<AlertProps["severity"]>("success");

  // Filter dropdown states
  const [filterModifier, setFilterModifier] = useState("");
  const [filterInsurance, setFilterInsurance] = useState("");
  const [filterDateRange, setFilterDateRange] = useState("");
  // Active status filter – default to showing only active rates
  const [filterActive, setFilterActive] = useState<string>("true");

  // Get data from Redux store
  const rates = useAppSelector((state: RootState) => state.ratesSlice.rates);
  const totalRecords = useAppSelector(
    (state: RootState) => state.ratesSlice.totalRecords
  );
  const loading = useAppSelector(
    (state: RootState) => state.ratesSlice.loading
  );
  const error = useAppSelector((state: RootState) => state.ratesSlice.error);

  // Get insurance providers from Redux store
  const insuranceProviders = useAppSelector(
    (state) => state.insuranceProviderSlice.insuranceProviders || []
  );

  const Allproviders = useAppSelector(
    (state) => state.providerListSlice.listData
  );

  // Auth Code catalog list
  const authCodes = useAppSelector(
    (state) =>
      (state as any).dataCatalogSlice.NewDataCatalog?.filter(
        (c: any) => c.catalogName === DataCatalogConcepts.AuthCode
      ) || []
  );

  // Modifier list from catalog
  const modifiers: any = useAppSelector((state: RootState) =>
    (state.dataCatalogSlice.NewDataCatalog || [])
      .filter((c: any) => c.catalogName === DataCatalogConcepts.Modifier)
      .map((c: any) => c.conceptValue)
  );

  // Fetch rates and insurance providers on component mount
  useEffect(() => {
    fetchRates();
    dispatch(getInsuranceProviders());
    dispatch(getDataCatalog());
    if (Allproviders.length === 0) {
      dispatch(GetAllProviders("full"));
    }
  }, [dispatch]);

  // Snackbar handlers
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Fetch rates from the API via Redux
  const fetchRates = () => {
    dispatch(
      getRates({
        modifier: filterModifier,
        insuranceProviderId: filterInsurance,
        dateRange: filterDateRange,
        active: filterActive,
        skip: 0,
        take: 10,
      })
    );
  };

  // Handle grid paging and other actions
  const onActionBegin = (args: any) => {
    if (args.requestType === "paging") {
      dispatch(
        getRates({
          modifier: filterModifier,
          insuranceProviderId: filterInsurance,
          dateRange: filterDateRange,
          active: filterActive,
          skip: args.skip,
          take: args.take,
        })
      );
    }
  };

  // Row striping for Syncfusion grid
  const onRowDataBound = (args: RowDataBoundEventArgs) => {
    if (!args.row) return;

    // Syncfusion doesn't expose rowIndex directly on RowDataBoundEventArgs,
    // so we derive it from the DOM attribute added by the grid.
    const indexAttr = (args.row as HTMLElement).getAttribute("aria-rowindex");
    const index = indexAttr ? parseInt(indexAttr, 10) : 0;

    if (index % 2 !== 0) {
      const rowElement = args.row as HTMLTableRowElement;
      rowElement.style.backgroundColor = "rgb(236, 246, 255)";
    }
  };

  // Toggle active/inactive status and refresh list
  const handleToggleActive = async (rate: Rate, value: boolean) => {
    // Await the update so we only refresh after it succeeds
    await dispatch(updateRate({ id: rate.id, payload: { isActive: value } }));
    // Re-fetch with current filters so the grid stays in sync
    fetchRates();
  };

  // Template for Active switch
  // refetch whenever filters change
  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModifier, filterInsurance, filterDateRange, filterActive]);

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
  };

  // Handle select changes (for MUI Select components)
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name) {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
  };

  // Edit handler
  const handleEdit = (rate: Rate) => {
    setIsEditMode(true);
    setEditRateId(rate.id);
    setFormValues({
      authCode: rate.authCode,
      modifier: rate.modifier,
      perType: rate.perType,
      currency: rate.currency,
      rateAgreed: rate.rateAgreed,
      rateBilled: rate.rateBilled,
      employeeId: rate.employeeId,
      employeeRate: rate.employeeRate as any,
      insuranceProviderId: rate.insuranceProviderId,
      effectiveDate: moment(rate.effectiveDate).format("YYYY-MM-DD"),
      expirationDate: rate.expirationDate
        ? moment(rate.expirationDate).format("YYYY-MM-DD")
        : "",
    });
    setOpenDialog(true);
  };

  // Handle form submission
  const handleSubmit = () => {
    // Format data for API submission
    const payload = {
      ...formValues,
      rateBilled: Number(formValues.rateBilled),
      rateAgreed: Number(formValues.rateAgreed),
      employeeRate: Number(formValues.employeeRate),
    };

    try {
      if (isEditMode && editRateId) {
        dispatch(updateRate({ id: editRateId, payload }));
      } else {
        dispatch(createRate(payload));
      }

      setOpenDialog(false);
      setIsEditMode(false);
      setEditRateId(null);
      setFormValues(initialFormValues);

      setSnackbarSeverity("success");
      setSnackbarMsg(
        isEditMode ? "Rate updated successfully" : "Rate added successfully"
      );
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarSeverity("error");
      setSnackbarMsg(
        isEditMode ? "Failed to update rate" : "Failed to add rate"
      );
      setSnackbarOpen(true);
    }
  };

  // Handle rate deletion
  // const handleDeleteRate = () => {
  // 	if (!selectedRateId) return;

  // 	try {
  // 		dispatch(deleteRate(selectedRateId));
  // 		setDeleteConfirmOpen(false);
  // 		setSelectedRateId(null);

  // 		setSnackbarSeverity("success");
  // 		setSnackbarMsg("Rate deleted successfully");
  // 		setSnackbarOpen(true);
  // 	} catch (err) {
  // 		setSnackbarSeverity("error");
  // 		setSnackbarMsg("Failed to delete rate");
  // 		setSnackbarOpen(true);
  // 	}
  // };

  // Open delete confirmation dialog
  // const confirmDelete = (id: string) => {
  // 	setSelectedRateId(id);
  // 	setDeleteConfirmOpen(true);
  // };

  // Format date for display
  // const formatDate = (dateString: string) => {
  // 	return moment(dateString).format("MM/DD/YYYY");
  // };

  // Action template for grid
  const actionTemplate = (props: any) => {
    const rate = props as Rate;
    return (
      <div>
        <Switch
          checked={rate.isActive}
          onChange={(e) => handleToggleActive(rate, e.target.checked)}
          color="primary"
          size="small"
        />
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleEdit(rate)}
        >
          <EditIcon />
        </IconButton>
        {/* <IconButton
					size='small'
					color='primary'
					onClick={() => confirmDelete(rate.id)}
				>
					<DeleteIcon />
				</IconButton> */}
      </div>
    );
  };

  // Show loader any time we are in a loading state (consistent with other tabs)
  if (loading && rates.length === 0) {
    return <Loader isShow={true} />;
  }

  return (
    <div className="pageTemplate">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Rates</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Filters */}

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel> Status</InputLabel>
            <Select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              label="Active Status"
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Modifier</InputLabel>
            <Select
              value={filterModifier}
              onChange={(e) => setFilterModifier(e.target.value)}
              label="Modifier"
            >
              <MenuItem value="">All</MenuItem>
              {modifiers.map((m: any) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Insurance</InputLabel>
            <Select
              value={filterInsurance}
              onChange={(e) => setFilterInsurance(e.target.value)}
              label="Insurance"
            >
              <MenuItem value="">All</MenuItem>
              {insuranceProviders.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.insuranceProviderName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={filterDateRange}
              onChange={(e) => setFilterDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="Last7">Last 7 Days</MenuItem>
              <MenuItem value="Last15">Last 15 Days</MenuItem>
              <MenuItem value="Last30">Last 30 Days</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={() => {
              // reset to add-mode every time "Add New Rate" is pressed
              setIsEditMode(false);
              setEditRateId(null);
              setFormValues(initialFormValues);
              setOpenDialog(true);
            }}
            startIcon={<span>+</span>}
          >
            Add New Rate
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 1, bgcolor: "#ffebee", borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <GridComponent
        dataSource={rates}
        allowPaging={true}
        allowSorting={true}
        allowFiltering={true}
        filterSettings={{ type: "Menu" }}
        rowDataBound={onRowDataBound}
        actionBegin={onActionBegin}
        pageSettings={{ pageSize: 10, totalRecordsCount: totalRecords }}
        height={400}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="insuranceProvider.insuranceProviderName"
            headerText="Payer"
            width="150"
          />
          <ColumnDirective field="authCode" headerText="Code" width="100" />
          <ColumnDirective field="modifier" headerText="Modifier" width="120" />
          <ColumnDirective
            field="rateBilled"
            headerText="Company Rate"
            width="120"
            format="N2"
          />
          <ColumnDirective
            field="rateAgreed"
            headerText="Agreed Rate"
            width="120"
            format="N2"
          />
          <ColumnDirective field="perType" headerText="Per Type" width="100" />
          <ColumnDirective
            field="effectiveDate"
            headerText="Effective Date"
            width="120"
            type="date"
            format="MM/dd/yyyy"
          />
          <ColumnDirective
            field="expirationDate"
            headerText="Expiration Date"
            width="120"
            type="date"
            format="MM/dd/yyyy"
          />
          <ColumnDirective
            headerText="Actions"
            width="120"
            template={actionTemplate}
            textAlign="Center"
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort, Filter, Resize]} />
      </GridComponent>

      {/* Add New Rate Dialog */}
      <Dialog
        open={openDialog}
        className="largeModel largeModel--edit"
        onClose={() => setOpenDialog(false)}
        sx={{ m: 2 }}
      >
        <Box className="largeModel__head">
          <Typography variant="h5" className="largeModel__title">
            {isEditMode ? "Edit Rate" : "Add Rate"}
          </Typography>
          <Button
            className="largeModel__closeBtn"
            onClick={() => setOpenDialog(false)}
          >
            <CloseIcon />
          </Button>
        </Box>
        <Box className="largeModel__body" sx={{ px: 3, py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <FormControl fullWidth>
                <InputLabel id="payer-label">Payer</InputLabel>
                <Select
                  labelId="payer-label"
                  name="insuranceProviderId"
                  value={formValues.insuranceProviderId}
                  onChange={handleSelectChange}
                  label="Payer"
                >
                  {insuranceProviders.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.insuranceProviderName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <FormControl fullWidth>
                <InputLabel id="code-label">Code</InputLabel>
                <Select
                  labelId="code-label"
                  name="authCode"
                  value={formValues.authCode}
                  onChange={handleSelectChange}
                  label="Code"
                >
                  {authCodes.map((c: any) => (
                    <MenuItem key={c.conceptValue} value={c.conceptValue}>
                      {c.conceptValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <TextField
                fullWidth
                label="Rate Billed"
                name="rateBilled"
                type="number"
                value={formValues.rateBilled}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <TextField
                fullWidth
                label="Rate Agreed"
                name="rateAgreed"
                type="number"
                value={formValues.rateAgreed}
                onChange={handleInputChange}
              />
            </Grid>
            {/* <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
							<FormControl fullWidth>
								<InputLabel id='employee-label'>Employee</InputLabel>
								<Select
									labelId='employee-label'
									name='employeeId'
									value={formValues.employeeId}
									onChange={handleSelectChange}
									label='Employee'
								>
									<MenuItem value=''>None</MenuItem>
									{Allproviders.map((emp: any) => (
										<MenuItem
											key={emp.employmentDetails.id}
											value={emp.employmentDetails.id}
										>
											{emp.employmentDetails.firstName}{" "}
											{emp.employmentDetails.lastName}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
							<TextField
								fullWidth
								label='Employee Rate'
								name='employeeRate'
								type='number'
								value={formValues.employeeRate}
								onChange={handleInputChange}
							/>
						</Grid> */}
            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <FormControl fullWidth>
                <InputLabel id="per-type-label">Per Type</InputLabel>
                <Select
                  labelId="per-type-label"
                  name="perType"
                  value={formValues.perType}
                  onChange={handleSelectChange}
                  label="Per Type"
                >
                  <MenuItem value="Unit">Unit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <FormControl fullWidth>
                <InputLabel id="modifier-label">Modifier</InputLabel>
                <Select
                  labelId="modifier-label"
                  name="modifier"
                  value={formValues.modifier}
                  onChange={handleSelectChange}
                  label="Modifier"
                >
                  {modifiers.map((m: any) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <TextField
                fullWidth
                label="Effective Date"
                name="effectiveDate"
                type="date"
                value={formValues.effectiveDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6} style={{ marginTop: "10px" }}>
              <TextField
                fullWidth
                label="Expiration Date"
                name="expirationDate"
                type="date"
                value={formValues.expirationDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}
              style={{ marginTop: "10px" }}
            >
              <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                disabled={loading}
              >
                {isEditMode ? "Update Rate" : "Add Rate"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* <Dialog
				open={deleteConfirmOpen}
				className='smallModel'
				onClose={() => setDeleteConfirmOpen(false)}
			>
				<div>
					<DialogContent>Are you sure want to delete this rate ?</DialogContent>
					<DialogActions>
						<Button
							className='border-button'
							variant='contained'
							onClick={() => setDeleteConfirmOpen(false)}
							color='secondary'
						>
							No
						</Button>
						<Button
							className='button'
							variant='contained'
							onClick={handleDeleteRate}
							disabled={loading}
						>
							Yes
						</Button>
					</DialogActions>
				</div>
			</Dialog> */}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          elevation={6}
          variant="filled"
        >
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default BusinessRates;
